export class ClockTime {
  constructor(
    readonly hour24: number,
    readonly minute: number,
  ) {}

  static parse(raw: string): ClockTime | null {
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(raw)) return null;
    return new ClockTime(Number(raw.slice(0, 2)), Number(raw.slice(3, 5)));
  }

  static fromParts(hour24: number, minute: number): ClockTime | null {
    if (!Number.isInteger(hour24) || hour24 < 0 || hour24 > 23) return null;
    if (!Number.isInteger(minute) || minute < 0 || minute > 59) return null;
    return new ClockTime(hour24, minute);
  }

  static sanitizeHour(raw: string): string {
    const digits = raw.replace(/\D/g, "").slice(0, 2);
    if (!digits) return "";
    if (digits.length === 1) return digits;
    if (Number(digits) > 23) return digits[0];
    return digits;
  }

  static sanitizeMinute(raw: string): string {
    const digits = raw.replace(/\D/g, "").slice(0, 2);
    if (!digits) return "";
    if (digits.length === 1) return digits;
    if (Number(digits) > 59) return digits[0];
    return digits;
  }

  static hourReadyToAdvance(digits: string): boolean {
    if (digits.length === 2) {
      const n = Number(digits);
      return n >= 0 && n <= 23;
    }
    if (digits.length === 1) {
      const n = Number(digits);
      return n >= 3 && n <= 9;
    }
    return false;
  }

  toValue(): string {
    return `${String(this.hour24).padStart(2, "0")}:${String(this.minute).padStart(2, "0")}`;
  }
}

export class TimeValue {
  constructor(
    readonly hour: string,
    readonly minute: string,
  ) {}

  static parse(raw: string): TimeValue {
    const trimmed = raw.trim();
    if (!trimmed) return new TimeValue("", "");
    const complete = ClockTime.parse(trimmed);
    if (complete) {
      return new TimeValue(
        String(complete.hour24).padStart(2, "0"),
        String(complete.minute).padStart(2, "0"),
      );
    }
    const split = /^(\d{1,2})?:(\d{0,2})?$/.exec(trimmed);
    if (split) return new TimeValue(split[1] ?? "", split[2] ?? "");
    if (/^\d{1,2}$/.test(trimmed)) return new TimeValue(trimmed, "");
    return new TimeValue("", "");
  }

  get isEmpty(): boolean {
    return this.hour === "" && this.minute === "";
  }

  get isComplete(): boolean {
    if (!this.hasHour || this.minute.length !== 2) return false;
    return ClockTime.fromParts(Number(this.hour), Number(this.minute)) !== null;
  }

  get hasHour(): boolean {
    return this.hour !== "";
  }

  get hasFullMinutes(): boolean {
    if (this.minute.length !== 2) return false;
    const minute = Number(this.minute);
    return Number.isInteger(minute) && minute >= 0 && minute <= 59;
  }

  toStored(): string {
    if (this.isEmpty) return "";
    if (this.isComplete) {
      const clock = ClockTime.fromParts(Number(this.hour), Number(this.minute));
      return clock ? clock.toValue() : `${this.hour}:${this.minute}`;
    }
    if (this.hasHour && this.minute === "") return `${this.hour}:`;
    if (!this.hasHour) return `:${this.minute}`;
    return `${this.hour}:${this.minute}`;
  }
}

export class TimeFieldValidator {
  message(raw: string, emptyMessage: string, minutesMessage = "Enter the minutes.", completeMessage = "Enter a complete time."): string | undefined {
    const time = TimeValue.parse(raw);
    if (time.isEmpty) return emptyMessage;
    if (time.isComplete) return undefined;
    if (time.hasHour && !time.hasFullMinutes) return minutesMessage;
    return completeMessage;
  }
}
